/**
 * NoteDocSpawnContext — exposes the connection coordinates the
 * `useNoteDoc` hook needs to spawn a per-note y-partyserver provider.
 *
 * Two providers populate this context:
 *   - The user-do connection provider sets `{ party: "user-do", room: userId, ...}`
 *     for the personal scope.
 *   - The study-group provider sets `{ party: "study-group-do", room: groupId, ...}`
 *     for shared decks.
 *
 * Both keep the same per-note registry — see `note-doc-registry.ts` —
 * so refcounts compose across whatever subtree mounted the editor.
 *
 * Context value is `null` when the relevant connection isn't ready
 * (signed out / not yet hydrated). Hooks that read this context return
 * empty/null in that case so the rest of the UI keeps rendering.
 */

import React, { createContext, useContext, useMemo } from 'react';

export interface NoteDocSpawnContextValue {
  party: string;
  room: string;
  host: string;
  /** Optional auth-token resolver. Mirrors y-partyserver's `params`. */
  getToken?: () => Promise<string | null>;
}

const NoteDocSpawnContext = createContext<NoteDocSpawnContextValue | null>(null);

export function NoteDocSpawnProvider({
  value,
  children,
}: {
  value: NoteDocSpawnContextValue | null;
  children: React.ReactNode;
}): React.ReactElement {
  // Memoise so identity-stable consumers don't re-render on every parent
  // render (e.g. when getToken is created inline upstream).
  const stable = useMemo<NoteDocSpawnContextValue | null>(
    () =>
      value
        ? {
            party: value.party,
            room: value.room,
            host: value.host,
            getToken: value.getToken,
          }
        : null,
    [value?.party, value?.room, value?.host, value?.getToken],
  );
  return (
    <NoteDocSpawnContext.Provider value={stable}>{children}</NoteDocSpawnContext.Provider>
  );
}

/**
 * Read the spawn config for the closest provider — personal scope by
 * default, group scope inside a `<StudyGroupProvider>`. Returns `null`
 * when no provider is mounted (signed out or pre-hydration).
 */
export function useNoteDocSpawnConfig(): NoteDocSpawnContextValue | null {
  return useContext(NoteDocSpawnContext);
}
